import { useMemo, useState } from 'react';
import { CampaignSummary, CampaignType } from '../../lib/types';
import { parseDate, today, toISODate } from '../../lib/dates';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';

interface Props {
  campaigns: CampaignSummary[];
  onCampaignClick: (id: string) => void;
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const typeColor = (t: CampaignType): string =>
  t === 'Promotion' ? 'var(--sitewide)' : t === 'Brand' ? 'var(--brand)' : 'var(--seasonal)';

const typeBg = (t: CampaignType): string =>
  t === 'Promotion' ? 'var(--sitewide-bg)' : t === 'Brand' ? 'var(--brand-bg)' : 'var(--seasonal-bg)';

const buildMonthWeeks = (year: number, month: number): Date[][] => {
  const first = new Date(year, month, 1);
  const dow = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - dow);
  const weeks: Date[][] = [];
  for (let w = 0; w < 6; w++) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(gridStart);
      day.setDate(gridStart.getDate() + w * 7 + d);
      week.push(day);
    }
    weeks.push(week);
    const last = week[6];
    if (last.getMonth() !== month && last >= first) break;
  }
  return weeks;
};

interface PlacedBar {
  campaign: CampaignSummary;
  startCol: number;
  span: number;
  lane: number;
  continuesLeft: boolean;
  continuesRight: boolean;
}

const placeBars = (weekDays: Date[], campaigns: CampaignSummary[]): PlacedBar[] => {
  const weekStart = weekDays[0];
  const weekEnd = weekDays[6];
  // Undated campaigns ("To plan") don't appear on the calendar.
  const overlapping = campaigns
    .filter((c): c is CampaignSummary & { start_date: string; end_date: string } =>
      !!c.start_date && !!c.end_date)
    .map((c) => ({ c, s: parseDate(c.start_date), e: parseDate(c.end_date) }))
    .filter(({ s, e }) => e >= weekStart && s <= weekEnd)
    .sort((a, b) => a.s.getTime() - b.s.getTime() || b.e.getTime() - a.e.getTime());

  const lanes: PlacedBar[][] = [];
  const placed: PlacedBar[] = [];
  for (const { c, s, e } of overlapping) {
    const startIdx = Math.max(0, Math.round((s.getTime() - weekStart.getTime()) / 86400000));
    const endIdx = Math.min(6, Math.round((e.getTime() - weekStart.getTime()) / 86400000));
    const span = endIdx - startIdx + 1;
    let lane = 0;
    while (true) {
      const occupied = (lanes[lane] ?? []).some(
        (b) => !(b.startCol + b.span - 1 < startIdx || b.startCol > endIdx),
      );
      if (!occupied) break;
      lane++;
    }
    const bar: PlacedBar = {
      campaign: c,
      startCol: startIdx,
      span,
      lane,
      continuesLeft: s < weekStart,
      continuesRight: e > weekEnd,
    };
    if (!lanes[lane]) lanes[lane] = [];
    lanes[lane].push(bar);
    placed.push(bar);
  }
  return placed;
};

export const CampaignCalendar = ({ campaigns, onCampaignClick }: Props) => {
  const [cursor, setCursor] = useState<Date>(() => {
    const t = today();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });

  const weeks = useMemo(
    () => buildMonthWeeks(cursor.getFullYear(), cursor.getMonth()),
    [cursor],
  );

  const todayIso = toISODate(today());
  const monthLabel = cursor.toLocaleDateString('en', { month: 'long', year: 'numeric' });

  const weekRows = weeks.map((days, idx) => ({
    days,
    bars: placeBars(days, campaigns),
    key: idx,
  }));

  const barRowHeight = 18;
  const barRowGap = 2;
  const cellTopPad = 22;
  const minCellHeight = 96;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, marginBottom: 10 }}>
        <button
          type="button"
          className="icon-btn"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          title="Previous month"
        >
          <Icon name="chevronLeft" size={14} />
        </button>
        <div className="serif" style={{ fontSize: 24 }}>{monthLabel}</div>
        <button
          type="button"
          className="icon-btn"
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          title="Next month"
        >
          <Icon name="chevronRight" size={14} />
        </button>
        <Button
          variant="ghost"
          onClick={() => {
            const t = today();
            setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
          }}
        >
          Today
        </Button>
      </div>

      <div className="cal" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {DOW.map((d) => (
          <div key={d} className="cal-dow">{d}</div>
        ))}
        {weekRows.map(({ days, bars, key }) => {
          const lanes = bars.reduce((m, b) => Math.max(m, b.lane + 1), 0);
          const rowHeight = Math.max(
            minCellHeight,
            cellTopPad + lanes * (barRowHeight + barRowGap) + 8,
          );
          return (
            <div
              key={key}
              style={{
                gridColumn: '1 / -1',
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                position: 'relative',
                minHeight: rowHeight,
              }}
            >
              {days.map((d) => {
                const iso = toISODate(d);
                const outside = d.getMonth() !== cursor.getMonth();
                const isToday = iso === todayIso;
                return (
                  <div
                    key={iso}
                    className={`cal-cell ${outside ? 'outside' : ''} ${isToday ? 'today' : ''}`}
                    style={{ minHeight: rowHeight }}
                  >
                    <div className="cal-date">{d.getDate()}</div>
                  </div>
                );
              })}
              {bars.map((b) => {
                const isPast = parseDate(b.campaign.end_date as string) < today();
                const left = `calc(${(b.startCol / 7) * 100}% + 4px)`;
                const width = `calc(${(b.span / 7) * 100}% - 8px)`;
                const top = cellTopPad + b.lane * (barRowHeight + barRowGap);
                return (
                  <button
                    type="button"
                    key={`${b.campaign.id}-${b.startCol}`}
                    onClick={() => onCampaignClick(b.campaign.id)}
                    className="cal-campaign-bar"
                    style={{
                      position: 'absolute',
                      top,
                      left,
                      width,
                      height: barRowHeight,
                      background: isPast ? 'transparent' : typeBg(b.campaign.type),
                      border: `1px solid ${typeColor(b.campaign.type)}`,
                      color: typeColor(b.campaign.type),
                      borderRadius: 4,
                      borderTopLeftRadius: b.continuesLeft ? 0 : 4,
                      borderBottomLeftRadius: b.continuesLeft ? 0 : 4,
                      borderTopRightRadius: b.continuesRight ? 0 : 4,
                      borderBottomRightRadius: b.continuesRight ? 0 : 4,
                      borderLeftStyle: b.continuesLeft ? 'none' : 'solid',
                      borderRightStyle: b.continuesRight ? 'none' : 'solid',
                      padding: '0 6px',
                      fontSize: 11,
                      fontWeight: 500,
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      opacity: isPast ? 0.6 : 1,
                    }}
                    title={`${b.campaign.name} · ${b.campaign.type}`}
                  >
                    {b.continuesLeft ? '… ' : ''}
                    {b.campaign.name}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CampaignCalendar;
